/**
 * Iter208.67 + Iter209l — Anteprima live del sito prima della pubblicazione.
 *
 * Renderizza il template scelto (Atrium / Casa Vicina / Villa Reale / Meridian
 * / Bottega Case) usando lo stesso pattern di PublicSite.jsx. I dati vengono
 * caricati da `GET /public/site/:slug` (dove `slug` = user id del pro) così
 * la parity con la produzione è garantita.
 *
 * Il routing interno dei template (`useSiteLocation`/`useSiteNavigate`) è
 * ancorato a un `PreviewNavContext` in-memory che permette la navigazione tra
 * home/annunci/chi-siamo/contatti senza cambiare l'URL del browser.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Info, GearSix, CaretDown } from "@phosphor-icons/react";

import AtriumSite from "@/pages/public/AtriumSite";
import CasaVicinaSite from "@/pages/public/CasaVicinaSite";
import VillaRealeSite from "@/pages/public/VillaRealeSite";
import MeridianSite from "@/pages/public/MeridianSite";
import BottegaCaseSite from "@/pages/public/BottegaCaseSite";
import { PreviewNavContext } from "@/lib/previewNav";
import { SiteEditProvider, SiteEditToolbar } from "@/lib/siteEditing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SitePreview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Iter209n — Override template da URL (`?template=X`) per confronto
  // tra template in nuova scheda. Non viene salvato in DB: sovrascrive
  // solo il rendering di questa preview.
  const templateOverride = (searchParams.get("template") || "").trim();
  const [data, setData] = useState(null);
  const [slug, setSlug] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Slug = id dell'utente pro corrente
        const me = await api.get("/auth/me");
        const s = me.data?.id || me.data?.sub || me.data?._id;
        if (!s) throw new Error("Utente non identificato");
        setSlug(s);
        const r = await axios.get(`${API}/public/site/${s}`);
        setData(r.data);
      } catch (e) {
        toast.error("Impossibile caricare l'anteprima del sito");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Iter209l — Routing in-memory per l'anteprima. I template navigano usando
  // `useSiteLocation()` / `useSiteNavigate()` con il prefisso `/site/{slug}`.
  const [siteInternalPath, setSiteInternalPath] = useState("");
  const pathname = `/site/${slug}${siteInternalPath ? "/" + siteInternalPath : ""}`;
  // Iter209o — Page-selector: la sezione corrente è il primo segmento del
  // sub-path (es. `annunci/abc` → `annunci`). Usiamo il valore normalizzato
  // per evidenziare la voce giusta nel dropdown anche in viste dettaglio.
  const currentSection = (siteInternalPath.split("/")[0] || "").trim();
  const previewNav = useMemo(
    () => ({
      pathname,
      navigate: (to) => {
        if (typeof to !== "string") return;
        // I template usano to come URL assoluto sul sito.
        // Rimuoviamo il prefisso `/site/{slug}` per ottenere solo il sub-path.
        let sub = to.replace(`/site/${slug}`, "");
        sub = sub.replace(/^\/+/, "");
        setSiteInternalPath(sub);
        try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_e) {}
      },
    }),
    [pathname, slug],
  );

  // SEO — inject <title>/description/keywords (come prima)
  const seo = data?.site_config?.seo || {};
  const seoTitle = seo.title || "";
  const seoDesc = seo.description || "";
  const seoKws = (seo.keywords || []).join(",");
  useEffect(() => {
    const prevTitle = document.title;
    if (seoTitle) document.title = seoTitle;
    const upsertMeta = (name, content) => {
      if (!content) return null;
      let el = document.head.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return () => { if (prev !== null) el.setAttribute("content", prev); else el.remove(); };
    };
    const cleanDesc = upsertMeta("description", seoDesc);
    const cleanKw = upsertMeta("keywords", seoKws);
    return () => {
      document.title = prevTitle;
      if (cleanDesc) cleanDesc();
      if (cleanKw) cleanKw();
    };
  }, [seoTitle, seoDesc, seoKws]);

  if (loading) {
    return <div className="p-10 text-center text-slate-500" data-testid="site-preview-loading">Caricamento anteprima…</div>;
  }
  if (!data) {
    return <div className="p-10 text-center text-slate-500" data-testid="site-preview-empty">Anteprima non disponibile.</div>;
  }

  // Iter209l — Selezione template (stesso if/else di PublicSite.jsx).
  // Iter209n — Se ?template=X è in URL, usa quello (override in-memory).
  const templateId = (templateOverride || data.site_config?.template_id || "").trim();
  let TemplateCmp = AtriumSite;
  if (templateId === "casa-vicina") TemplateCmp = CasaVicinaSite;
  else if (templateId === "villa-reale") TemplateCmp = VillaRealeSite;
  else if (templateId === "meridian") TemplateCmp = MeridianSite;
  else if (templateId === "bottega-case") TemplateCmp = BottegaCaseSite;

  // Iter209m — Edit-in-preview: il pro modifica testi + nasconde sezioni
  // direttamente dall'anteprima senza uscire dal wizard.
  // Iter209n — In modalità "Prova questo" (override via URL) disabilitiamo
  // l'editor: le modifiche non devono persistere se il template mostrato
  // non è quello effettivamente scelto/salvato dal pro.
  const token = typeof window !== "undefined" ? localStorage.getItem("ht_token") : null;
  const editEnabled = !templateOverride;

  return (
    <PreviewNavContext.Provider value={previewNav}>
      <SiteEditProvider
        enable={editEnabled}
        initialEdits={data.site_config?.text_edits || {}}
        initialHidden={data.site_config?.hidden_sections || []}
        token={editEnabled ? token : null}
      >
        <div data-testid="site-preview" data-template-id={templateId || "default"} data-template-override={templateOverride || undefined}>
          {/* Toolbar preview */}
          <div className="sticky top-0 z-[60] bg-slate-900 text-white text-xs px-3 py-1.5 flex items-center justify-between gap-2 shadow" data-testid="site-preview-toolbar">
            <button onClick={() => navigate("/site-wizard")} className="inline-flex items-center gap-1 hover:opacity-80 shrink-0" data-testid="site-preview-back-btn">
              <ArrowLeft size={12} /> Modifica sito
            </button>

            {/* Iter209o — Page selector: salta tra Home / Annunci / Chi siamo / Contatti */}
            <label className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 rounded px-2 py-0.5 cursor-pointer transition-colors relative" data-testid="site-preview-page-selector-wrap">
              <span className="opacity-70">Vai a:</span>
              <select
                value={currentSection}
                onChange={(e) => previewNav.navigate(`/site/${slug}${e.target.value ? "/" + e.target.value : ""}`)}
                className="bg-transparent text-white text-xs pr-4 focus:outline-none appearance-none cursor-pointer"
                data-testid="site-preview-page-selector"
                aria-label="Salta a pagina"
              >
                <option value="" className="text-slate-900">Home</option>
                <option value="annunci" className="text-slate-900">Annunci</option>
                <option value="chi-siamo" className="text-slate-900">Chi siamo</option>
                <option value="contatti" className="text-slate-900">Contatti</option>
              </select>
              <CaretDown size={10} className="opacity-70 -ml-3 pointer-events-none" />
            </label>

            <div className="inline-flex items-center gap-1 opacity-70 flex-1 min-w-0 justify-center truncate" data-testid="site-preview-toolbar-info">
              <Info size={12} className="shrink-0" />
              <span className="truncate">
                {templateOverride
                  ? `Anteprima "${templateOverride}" — non salvata, solo confronto`
                  : "Anteprima privata — clicca sui testi per modificarli"}
              </span>
            </div>
            <button onClick={() => navigate("/settings?tab=abbonamenti")} className="inline-flex items-center gap-1 hover:opacity-80 shrink-0" data-testid="site-preview-domain-btn">
              <GearSix size={12} /> Dominio
            </button>
          </div>
          {/* Iter209m — Barra editor sopra il template (solo se edit abilitato) */}
          {editEnabled && <SiteEditToolbar />}
          <TemplateCmp slug={slug} initialData={data} />
        </div>
      </SiteEditProvider>
    </PreviewNavContext.Provider>
  );
}
